import { ExpressionLogicalOperatorEnum, SubClause, WhereClause } from "../ars_ldm";
import { AnnotationVariable } from "../contexts/interface";
import {
  isSubClauseWithCompoundExpression,
  isSubClauseWithCondition,
  isSubClauseWithReference,
  isWhereClauseWithCompoundExpression,
  isWhereClauseWithCondition,
} from "../typeGuards";
import { formatConditionAsCode } from "./formatConditionAsCode";

/**
 * Formats a where clause as code for compact display.
 * Handles both simple conditions and compound expressions with logical operators and brackets.
 *
 * @param whereClause - The where clause or sub-clause to format.
 * @param availableVariables - List of available variables with dataType information.
 * @returns Formatted string like "ADSL.AGE = 18" or "(ADSL.SAFFL = 'Y' AND ADSL.AGE > 18)".
 */
export const formatWhereClauseAsCode = (
  whereClause: WhereClause | SubClause,
  availableVariables?: AnnotationVariable[],
): string => {
  if (isSubClauseWithReference(whereClause) && whereClause.subClauseId !== "") {
    return `[${whereClause.subClauseId}]`;
  }

  // Handle simple condition
  else if (isWhereClauseWithCondition(whereClause) || isSubClauseWithCondition(whereClause)) {
    return formatConditionAsCode(whereClause.condition, availableVariables);
  }

  // Handle compound expression
  else if (
    isWhereClauseWithCompoundExpression(whereClause) ||
    isSubClauseWithCompoundExpression(whereClause)
  ) {
    const { logicalOperator, whereClauses } = whereClause.compoundExpression;

    if (!whereClauses || whereClauses.length === 0) {
      return "";
    }

    // Format each sub-clause
    const formattedClauses = whereClauses
      .map((subClause) => formatSubClause(subClause, availableVariables))
      .filter((clause) => clause !== "");

    if (formattedClauses.length === 0) {
      return "";
    }

    // Handle NOT operator
    if (logicalOperator === ExpressionLogicalOperatorEnum.NOT) {
      const clause = formattedClauses[0];
      // Clause is either already bracketed or a simple condition
      return `NOT ${clause}`;
    }

    // Handle AND/OR operators
    const operator = logicalOperator === ExpressionLogicalOperatorEnum.AND ? " AND " : " OR ";
    const combined = formattedClauses.join(operator);

    // Wrap in brackets for compound expressions
    return `(${combined})`;
  }

  return "";
};

/**
 * Formats a sub-clause (part of a compound expression) as code.
 *
 * @param subClause - The sub-clause to format.
 * @param availableVariables - List of available variables with dataType information.
 * @returns Formatted string for the sub-clause.
 */
const formatSubClause = (
  subClause: SubClause,
  availableVariables?: AnnotationVariable[],
): string => {
  // Handle referenced where clause (subClauseId)
  /* istanbul ignore else  */
  if (isSubClauseWithReference(subClause) && subClause.subClauseId !== "") {
    return `[${subClause.subClauseId}]`;
  }

  // Handle simple condition
  else if (isSubClauseWithCondition(subClause)) {
    return formatConditionAsCode(subClause.condition, availableVariables);
  }

  // Handle nested compound expression
  else if (isSubClauseWithCompoundExpression(subClause)) {
    const { logicalOperator, whereClauses } = subClause.compoundExpression;

    if (!whereClauses || whereClauses.length === 0) {
      return "";
    }

    const formattedClauses = whereClauses
      .map((sc) => formatSubClause(sc, availableVariables))
      .filter((clause) => clause !== "");

    if (formattedClauses.length === 0) {
      return "";
    }

    // Handle NOT operator
    if (logicalOperator === ExpressionLogicalOperatorEnum.NOT) {
      const clause = formattedClauses[0];
      // Clause is either already bracketed or a simple condition
      return `NOT ${clause}`;
    }

    // Handle AND/OR operators
    const operator = logicalOperator === ExpressionLogicalOperatorEnum.AND ? " AND " : " OR ";
    return `(${formattedClauses.join(operator)})`;
  }

  // istanbul ignore next
  return "";
};
